import React, { useState, useCallback } from 'react';
import { fileDatabaseAPI } from '../services/api';
import { Database, Search, AlertTriangle, CheckCircle, Info, Loader2, FolderOpen, Copy, ChevronDown, ChevronUp, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const ConfidenceBadge = ({ confidence, level }) => {
  const getColor = () => {
    if (confidence >= 95) return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
    if (confidence >= 80) return 'bg-green-500/20 text-green-400 border-green-500/30';
    if (confidence >= 60) return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
    return 'bg-red-500/20 text-red-400 border-red-500/30';
  };
  const getIcon = () => {
    if (confidence >= 95) return <Shield className="w-3 h-3" />;
    if (confidence >= 80) return <CheckCircle className="w-3 h-3" />;
    if (confidence >= 60) return <Info className="w-3 h-3" />;
    return <AlertTriangle className="w-3 h-3" />;
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold border ${getColor()}`} data-testid={`confidence-badge-${level}`}>
      {getIcon()} {confidence}%
    </span>
  );
};

const MatchCard = ({ match, index }) => {
  const [expanded, setExpanded] = useState(false);

  const copyPath = () => {
    const folder = match.source_folder || '';
    const file = match.project_file || '';
    const path = folder ? `${folder}\\${file}` : file;
    navigator.clipboard.writeText(path);
    toast.success('Pfad kopiert!');
  };

  return (
    <div
      className={`border rounded-lg p-3 transition-all hover:border-[#EC2229]/30 ${
        match.confidence >= 95 ? 'border-emerald-500/30 bg-emerald-500/5' :
        match.confidence >= 80 ? 'border-green-500/20 bg-green-500/5' :
        match.confidence >= 60 ? 'border-amber-500/20 bg-amber-500/5' :
        'border-white/10 bg-white/5'
      }`}
      data-testid={`match-card-${index}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <ConfidenceBadge confidence={match.confidence} level={match.match_level} />
            <span className="text-xs text-slate-500">Level {match.match_level}</span>
            {match.vehicle_manufacturer && (
              <span className="text-xs text-slate-400 font-medium">{match.vehicle_manufacturer}</span>
            )}
            {match.vehicle_series && (
              <span className="text-xs text-slate-500">{match.vehicle_series}</span>
            )}
            {match.engine_variant && (
              <span className="text-xs text-slate-500">{match.engine_variant}</span>
            )}
          </div>
          <div className="flex items-center gap-2 mb-1">
            {match.ecu_type && <span className="text-xs font-mono bg-white/10 px-1.5 py-0.5 rounded text-blue-300">{match.ecu_type}</span>}
            {match.ecu_nr_prod && <span className="text-xs font-mono text-slate-400">{match.ecu_nr_prod}</span>}
            {match.software_version && <span className="text-xs font-mono text-slate-500">SW:{match.software_version}</span>}
          </div>
          <p className="text-xs text-emerald-400/80 italic">{match.match_reason}</p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={copyPath} className="p-1.5 hover:bg-white/10 rounded transition-colors" title="Pfad kopieren" data-testid={`copy-path-${index}`}>
            <Copy className="w-3.5 h-3.5 text-slate-400" />
          </button>
          <button onClick={() => setExpanded(!expanded)} className="p-1.5 hover:bg-white/10 rounded transition-colors" data-testid={`toggle-details-${index}`}>
            {expanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-2 pt-2 border-t border-white/5 space-y-1.5" data-testid={`match-details-${index}`}>
          <div className="flex items-center gap-2 bg-black/20 rounded p-2">
            <FolderOpen className="w-3.5 h-3.5 text-[#EC2229] flex-shrink-0" />
            <span className="text-xs font-mono text-slate-300 break-all">{match.source_folder}\\{match.project_file}</span>
          </div>
          <div className="grid grid-cols-2 gap-1 text-xs">
            {match.ecu_manufacturer && <div><span className="text-slate-500">ECU Hersteller:</span> <span className="text-slate-300">{match.ecu_manufacturer}</span></div>}
            {match.ecu_nr_ecu && <div><span className="text-slate-500">ECU-Nr:</span> <span className="text-slate-300 font-mono">{match.ecu_nr_ecu}</span></div>}
            {match.sw_version && <div><span className="text-slate-500">SW-Version:</span> <span className="text-slate-300 font-mono">{match.sw_version}</span></div>}
            {match.project_size && <div><span className="text-slate-500">Größe:</span> <span className="text-slate-300">{match.project_size}</span></div>}
            {match.fuel_type && <div><span className="text-slate-500">Kraftstoff:</span> <span className="text-slate-300">{match.fuel_type}</span></div>}
            {match.project_type && <div><span className="text-slate-500">Projektart:</span> <span className="text-slate-300">{match.project_type}</span></div>}
            <div><span className="text-slate-500">Quelle:</span> <span className="text-slate-300">{match.source_file}</span></div>
          </div>
          {match.version_types?.length > 0 && (
            <div className="flex gap-1 flex-wrap">
              {match.version_types.map((v, i) => (
                <span key={i} className="text-xs px-1.5 py-0.5 bg-white/5 border border-white/10 rounded text-slate-400">{v}</span>
              ))}
            </div>
          )}
          {match.matched_fields?.length > 0 && (
            <div className="text-xs text-slate-500">
              Übereinstimmende Felder: <span className="text-emerald-400">{match.matched_fields.join(', ')}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const FileMatchingPanel = ({ fileId }) => {
  const [matches, setMatches] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [minConfidence, setMinConfidence] = useState(0);

  const runMatching = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fileDatabaseAPI.matchFile(fileId, { max_results: 100, min_confidence: minConfidence });
      setMatches(res.data.matches);
      setSearchParams(res.data.search_params);
      setExpanded(true);
      if (res.data.matches.length === 0) {
        toast.info('Keine passenden Projekte gefunden');
      } else {
        toast.success(`${res.data.matches.length} passende Projekte gefunden`);
      }
    } catch (err) {
      console.error('Matching failed:', err);
      toast.error('Fehler beim File-Matching');
    } finally {
      setLoading(false);
    }
  }, [fileId, minConfidence]);

  if (!expanded && !matches) {
    return (
      <button
        onClick={runMatching}
        className="glass-card p-4 w-full text-left hover:border-[#EC2229]/30 transition-all group"
        data-testid="file-matching-trigger"
        disabled={loading}
      >
        <div className="flex items-center gap-3">
          <div className="bg-[#EC2229]/10 p-2 rounded-lg">
            {loading ? <Loader2 className="w-5 h-5 text-[#EC2229] animate-spin" /> : <Database className="w-5 h-5 text-[#EC2229]" />}
          </div>
          <div>
            <p className="text-sm font-medium text-white group-hover:text-[#EC2229] transition-colors">
              {loading ? 'Suche läuft...' : 'Passende Projekte suchen'}
            </p>
            <p className="text-xs text-slate-500">2,5 Mio. Projekte durchsuchen (Datenbank-Matching)</p>
          </div>
        </div>
      </button>
    );
  }

  const highConfidence = matches?.filter(m => m.confidence >= 80) || [];
  const medConfidence = matches?.filter(m => m.confidence >= 60 && m.confidence < 80) || [];
  const lowConfidence = matches?.filter(m => m.confidence < 60) || [];

  return (
    <div className="glass-card p-5" data-testid="file-matching-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
          <Database className="w-4 h-4 text-[#EC2229]" />
          Passende Projekte
          {matches && <span className="text-xs text-slate-500 font-normal">({matches.length} Treffer)</span>}
        </h3>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={runMatching} disabled={loading} className="text-xs h-7 px-2" data-testid="refresh-matches">
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
          </Button>
          <button onClick={() => setExpanded(!expanded)} className="text-xs text-slate-500 hover:text-white transition-colors">
            {expanded ? 'Einklappen' : 'Aufklappen'}
          </button>
        </div>
      </div>

      {expanded && (
        <>
          {/* Search params info */}
          {searchParams && (
            <div className="bg-black/20 rounded-lg p-2 mb-3 text-xs text-slate-400 flex flex-wrap gap-x-3 gap-y-1" data-testid="search-params">
              {searchParams.ecu_nr_prod && <span>Prod-Nr: <span className="text-white font-mono">{searchParams.ecu_nr_prod}</span></span>}
              {searchParams.ecu_nr_ecu && <span>ECU-Nr: <span className="text-white font-mono">{searchParams.ecu_nr_ecu}</span></span>}
              {searchParams.software_version && <span>SW: <span className="text-white font-mono">{searchParams.software_version}</span></span>}
              {searchParams.ecu_type && <span>ECU: <span className="text-white font-mono">{searchParams.ecu_type}</span></span>}
              {searchParams.vehicle_manufacturer && <span>Marke: <span className="text-white">{searchParams.vehicle_manufacturer}</span></span>}
              {searchParams.file_size && <span>Größe: <span className="text-white">{(searchParams.file_size / 1024).toFixed(0)} KB</span></span>}
            </div>
          )}

          {/* Filter */}
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs text-slate-500">Min. Konfidenz:</span>
            {[0, 60, 80, 95].map(v => (
              <button
                key={v}
                onClick={() => { setMinConfidence(v); }}
                className={`text-xs px-2 py-1 rounded border transition-all ${minConfidence === v ? 'bg-[#EC2229]/20 border-[#EC2229]/30 text-[#EC2229]' : 'bg-white/5 border-white/10 text-slate-500 hover:text-white'}`}
                data-testid={`filter-confidence-${v}`}
              >
                {v === 0 ? 'Alle' : `${v}%+`}
              </button>
            ))}
          </div>

          {/* Results */}
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-[#EC2229] animate-spin" />
              <span className="ml-2 text-sm text-slate-400">Durchsuche 2,5 Mio. Projekte...</span>
            </div>
          ) : matches && matches.length > 0 ? (
            <div className="space-y-4 max-h-[600px] overflow-y-auto pr-1">
              {highConfidence.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-3.5 h-3.5 text-emerald-400" />
                    <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">Hohe Sicherheit ({highConfidence.length})</span>
                  </div>
                  <div className="space-y-2">
                    {highConfidence.filter(m => m.confidence >= minConfidence).map((m, i) => <MatchCard key={`h-${i}`} match={m} index={i} />)}
                  </div>
                </div>
              )}
              {medConfidence.length > 0 && minConfidence < 80 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Info className="w-3.5 h-3.5 text-amber-400" />
                    <span className="text-xs font-semibold text-amber-400 uppercase tracking-wider">Mittlere Sicherheit ({medConfidence.length})</span>
                  </div>
                  <div className="space-y-2">
                    {medConfidence.filter(m => m.confidence >= minConfidence).map((m, i) => <MatchCard key={`m-${i}`} match={m} index={i + highConfidence.length} />)}
                  </div>
                </div>
              )}
              {lowConfidence.length > 0 && minConfidence < 60 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
                    <span className="text-xs font-semibold text-red-400 uppercase tracking-wider">Nur zur Info ({lowConfidence.length})</span>
                  </div>
                  <div className="space-y-2">
                    {lowConfidence.filter(m => m.confidence >= minConfidence).map((m, i) => <MatchCard key={`l-${i}`} match={m} index={i + highConfidence.length + medConfidence.length} />)}
                  </div>
                </div>
              )}
            </div>
          ) : matches && matches.length === 0 ? (
            <div className="text-center py-6">
              <Database className="w-8 h-8 text-slate-600 mx-auto mb-2" />
              <p className="text-sm text-slate-500">Keine passenden Projekte gefunden</p>
              <p className="text-xs text-slate-600 mt-1">Die Suchparameter ergaben keine Treffer in der Datenbank</p>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default FileMatchingPanel;
